import React from "react";
import {
  formatAiUsageAud,
  formatAiUsageCount,
  formatFailedRate,
  formatLatencyMs,
} from "./aiUsageOverview.utils";

const InsightCard = ({ label, value, hint }) => (
  <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
    <p className="mt-1 text-lg font-semibold text-gray-900">{value}</p>
    {hint ? <p className="mt-0.5 text-xs text-gray-500">{hint}</p> : null}
  </div>
);

const AiUsageInsightsStrip = ({ totals = {} }) => {
  const hasInsights =
    totals.avg_cost_per_call_aud != null ||
    totals.failed_rate_pct != null ||
    totals.avg_latency_ms != null ||
    totals.avg_turn_ms != null ||
    totals.billed_count > 0 ||
    totals.cached_count > 0;

  if (!hasInsights) {
    return null;
  }

  return (
    <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
      <InsightCard
        label="Avg cost / call"
        value={
          totals.avg_cost_per_call_aud != null
            ? formatAiUsageAud(totals.avg_cost_per_call_aud, 4)
            : "—"
        }
        hint="Mean billed AI credit per question"
      />
      <InsightCard
        label="Failure rate"
        value={formatFailedRate(totals.failed_rate_pct, totals.failed_count)}
        hint={
          totals.cached_count > 0
            ? `${formatAiUsageCount(totals.cached_count)} cached responses`
            : "Failed calls as share of total volume"
        }
      />
      {/*
        Two different clocks, deliberately shown side by side.

        "Response time" is what a person waited: one question, start to reply,
        including every model call, the SQL and the streaming. "Model time" is
        a single API round-trip. The second used to be labelled end-to-end,
        which made it impossible to agree with a tenant reporting slowness —
        it reads far lower because one answer runs several calls.
      */}
      <InsightCard
        label="Avg response time"
        value={formatLatencyMs(totals.avg_turn_ms)}
        hint={
          totals.p95_turn_ms != null
            ? `p95 ${formatLatencyMs(totals.p95_turn_ms)} · one question, send to reply`
            : "One question, send to reply"
        }
      />
      <InsightCard
        label="Avg model time"
        value={formatLatencyMs(totals.avg_latency_ms)}
        hint={
          totals.llm_call_count != null && totals.call_count > 0
            ? `${formatAiUsageCount(totals.llm_call_count)} model calls for ${formatAiUsageCount(totals.call_count)} questions`
            : "A single model round-trip"
        }
      />
      <InsightCard
        label="Billed calls"
        value={formatAiUsageCount(totals.billed_count)}
        hint={
          totals.call_count > 0
            ? `${formatAiUsageCount(totals.call_count)} total in range`
            : "Wallet-debited requests"
        }
      />
    </div>
  );
};

export default AiUsageInsightsStrip;
